import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Box, Select, MenuItem } from '@mui/material';
import { SCUIContent } from '../../../../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../../../atomic/molecules';
import { SCUILoadingLinear, SCUIDateRangerV2, SCUIButton } from '../../../../../atomic/atoms';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import TimeHelper from '../../../../../utils/time';
import { PlanningQuery } from '../../../../../services/studio/StudioQuery';
import { handleStudioApiResponse } from '../../../../helper/studio_helper';

export const PlanningList = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<any>();
    const [endDate, setEndDate] = useState<any>();
    const [studioId, setStudioId] = useState<any>('');
    const [studioOptions, setStudioOptions] = useState<any[]>([]);
    const [planList, setPlanList] = useState<any[]>([]);
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [rowCount, setRowCount] = useState<number>(0);

    const { t } = useTranslation();
    const history = useHistory();

    const columns = [
        {
            field: 'studioName',
            headerName: t('studio.studioName'),
            flex: 1,
            renderCell: (params: any) => <p className="studio-grid-text">{params.value}</p>,
        },
        {
            field: 'planingDate',
            headerName: t('studio.planning.planningDate'),
            flex: 1,
            renderCell: (params: any) => <p className="studio-grid-text">{TimeHelper.formatDate(params.value)}</p>,
        },
        {
            field: 'createdAt',
            headerName: t('studio.createdDate'),
            flex: 1,
            renderCell: (params: any) => <p className="studio-grid-text">{TimeHelper.formatDateHour(params.value)}</p>,
        },
        {
            field: 'createdBy',
            headerName: t('studio.createdBy'),
            flex: 1,
            renderCell: (params: any) => <p className="studio-grid-text">{params.value}</p>,
        },
        {
            field: 'id',
            flex: 1,
            headerName: t('general.edit'),
            renderCell: (params: any) => (
                <SCUIButton
                    text={t('general.edit')}
                    type="primary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={() => handleEdit(params.value)}
                    showStartIcon
                    startIcon={<EditIcon sx={{ color: '#fff' }} />}
                />
            ),
        },
    ];

    const handleChangeDate = (event: any) => {
        setStartDate(event[0]);
        setEndDate(event[1]);
    };

    const handleSearch = (pageSizeParam: any, pageNumber: any) => {
        setIsLoading(true);
        handleStudioApiResponse(
            PlanningQuery.GetPlannings({
                studioId: studioId !== '' ? studioId : null,
                planingDateBefore: endDate || new Date(),
                planingDateAfter: startDate || new Date(),
                page: pageNumber + 1,
                pageSize: pageSizeParam,
            }),
            (res: any) => {
                setPlanList(res.data?.items || []);
                setRowCount(res.data?.totalCount);
            },
            () => setIsLoading(false),
            t
        );
    };

    const handleAdd = () => {
        history.push('/studio/planning-create');
    };

    const handleEdit = (id: any) => {
        history.push('/studio/planning-detail/' + id);
    };

    useEffect(() => {
        setIsLoading(true);

        handleStudioApiResponse(
            PlanningQuery.GetPlanningPageData(),
            (res: any) => {
                setStudioOptions(res.data?.studios || []);
            },
            () => setIsLoading(false),
            t
        );

        handleSearch(10, 0);
    }, []);

    return (
        <SCUIContent>
            <Box sx={{ padding: '16px' }}>
                <SCUIBreadCrumb onlyTitle={t('studio.planning.header')} />
                <SCUILoadingLinear display={isLoading} />
                <Box sx={{ marginBottom: '16px', marginTop: '16px' }} className="form-input-container">
                    <SCUIButton
                        text={t('studio.planning.createPlanning')}
                        type="primary"
                        sizeType="fixed"
                        style={{ height: '35px' }}
                        size={'m'}
                        buttonType="button"
                        onClick={handleAdd}
                        showStartIcon
                        startIcon={<AddIcon sx={{ color: '#fff' }} />}
                    />
                </Box>
                <Box className="studio-form-paper row" sx={{ minWidth: '100%', marginTop: '8px' }}>
                    <Box className="form-input-container">
                        <p className="studio-form-label side">{t('studio.studioName')}:</p>
                        <Select
                            value={studioId}
                            onChange={(event: any) => {
                                setStudioId(event.target.value);
                            }}
                            sx={{ width: '250px' }}
                        >
                            {(studioOptions || []).map((studio: any) => (
                                <MenuItem key={studio.id} id={studio.id} value={studio.id}>
                                    {studio.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box className="form-input-container studio-date-container">
                        <p className="studio-form-label side">{t('studio.planning.planningDate')}:</p>
                        <SCUIDateRangerV2
                            fromDate={startDate}
                            toDate={endDate}
                            setDateRange={handleChangeDate}
                            dateFormat="YYYY-MM-DD"
                            containerStyling={{ width: '225px' }}
                        />
                    </Box>
                    <Box className="form-input-container">
                        <SCUIButton
                            text={t('general.search')}
                            type="primary"
                            sizeType="fixed"
                            style={{ height: '35px' }}
                            size={'m'}
                            buttonType="button"
                            onClick={() => handleSearch(pageSize, pageNumber)}
                        />
                    </Box>
                </Box>
                <Box sx={{ marginTop: '16px' }}>
                    <SCUICustomDataGrid
                        rows={planList || []}
                        columns={columns}
                        paginationModeChange
                        rowCount={rowCount}
                        onPageSizeChange={(i: number) => {
                            setPageSize(i);
                            setPageNumber(0);
                            handleSearch(i, pageNumber);
                        }}
                        pageSize={pageSize}
                        onPageChange={(i: number) => {
                            setPageNumber(i);
                            handleSearch(pageSize, i);
                        }}
                        page={pageNumber}
                    />
                </Box>
            </Box>
        </SCUIContent>
    );
};
